<template>
  <div :id="content.idHTML" class="form-conteneur" :class="jel_getClassObject(content)">
    <!-- tag="div" nécessaire pour accessibilité, éviter div dans un span -->
    <ValidationObserver
      ref="observer"
      v-slot="{ invalid, validate }"
      :vid="'vobserver' + uniqueId"
      tag="div"
    >
      <Message :content="null"></Message>
      <b-form
        :id="'idFormulaire' + uniqueId"
        @submit="onSubmit($event)"
        @input="validate({ silent: true })"
      >
        <EditableArea
          v-if="content.formulaire"
          :content="content.formulaire"
          :parentTemplateId="metadata['mgnl:template']"
        />

        <div
          v-if="!isNewRegistrationFlow()"
          :class="[
            getClassBoutonContinuer(),
            isBtnSecondaire
              ? 'd-flex justify-content-center justify-content-md-start'
              : 'flex-column',
          ]"
        >
          <!-- TODO: 2023-05-05 CIAM Temporaire -->
          <!-- TODO: xclass et xdisabled -->
          <!-- TODO: Trouver une façon élégante de ne valider que les champs visibles -->
          <b-button
            type="submit"
            :variant="btnVariant"
            class="btn w-xs-auto w-md-auto hauteur px-4 my-2"
            :class="{ valid: !invalid }"
            :disabled="invalid || isSubmitted || isSubmittedSecondaire"
          >
            <span data-ga="continuer" class="d-inline-flex w-auto">
              <div v-if="isSubmitted" class="mr-1">
                <b-spinner small :label="$t('chargement')"></b-spinner>
              </div>
              {{ content.libBoutonContinuer }}
            </span>
          </b-button>

          <!-- Ajouts pour formulaire Canafe -->
          <b-button
            v-if="isBtnSecondaire"
            :href="$t('messageComponent.detailsCompte')"
            :variant="btnVariantSecondaire"
            class="d-flex justify-content-center align-items-center w-xs-auto w-md-auto hauteur px-4 my-2 ml-2"
            :class="{ valid: !invalid }"
            :disabled="invalid || isSubmitted || isSubmittedSecondaire"
          >
            <span data-ga="continuer" class="d-inline-flex w-auto">
              <div v-if="isSubmittedSecondaire" class="mr-1">
                <b-spinner small :label="$t('chargement')"></b-spinner>
              </div>
              {{ libBtnSecondaire }}
            </span>
          </b-button>
          <!-- Ajouts pour formulaire validation Identité VerifiedMe -->
          <b-link
            v-else-if="isLienSecondaire"
            type="submit"
            class="lienSecondaire"
            :class="{ valid: !invalid }"
            :disabled="invalid || isSubmitted || isSubmittedSecondaire"
            @click="formSubmit(content.typeBoutonSecondaire.tacheSecondaire)"
          >
            <div data-ga="continuer" class="d-inline-flex w-auto">
              <div v-if="isSubmittedSecondaire" class="mr-1">
                <b-spinner small :label="$t('chargement')"></b-spinner>
              </div>
              {{ contentLienSecondaire }}
            </div>
          </b-link>
        </div>
      </b-form>
      <!-- Fenêtre modale de confirmation optionnelle -->
      <b-modal
        :id="confirmationUUID"
        :title="content.confTitre"
        header-class="px-3 py-1"
        centered
        hide-footer
        no-hide-backdrop
        no-close-on-backdrop
        no-close-on-esc
      >
        <template #modal-header="{ close }">
          <div v-if="content.confTitre" class="row posBtnFermer">
            <h3
              class="compteClair titre mx-xl-0 mx-lg-0 pt-2 pl-2 pr-4"
              v-html="jel_replaceTokens(content.confTitre)"
            />
            <button class="btnClose" @click="close()">
              <svg-icon
                icon-name="icone-fermer-mince"
                color="$couleur-primaire"
                className="iconeFermer"
              ></svg-icon>
            </button>
          </div>
        </template>

        <div class="compteClair d-flex flex-column align-items-center mb-2 mt-n2 px-1">
          <div v-html="jel_replaceTokens(content.confTexte)"></div>
          <div class="flex-column" :class="getClassBoutonContinuer()">
            <b-button
              class="btn btn-primaire px-4 mb-2"
              @click="$bvModal.hide(confirmationUUID), formSubmit(content.tache)"
            >
              {{ libBtnConfirmer }}
            </b-button>
          </div>

          <b-link class="lienSecondaire mt-0" @click="$bvModal.hide(confirmationUUID)">
            {{ libBtnModifier }}
          </b-link>
        </div>
      </b-modal>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { load } from 'recaptcha-v3';
import { EditableArea } from '@/vue-editor';

import Message from '@/components/base/Message';
import services from '@/services/index';

import {
  getCurrentLanguage,
  inMgnl,
  hasPromoSelected,
  isJelMobile,
  nettoyerLienExterne,
  promiseWrapper,
} from '@/helpers/AppHelpers';

import { gtmCustomEventByVendor } from '@/helpers/ConsentHelpers';

import { PageMapping } from '@/helpers/PageMappingHelpers';
import { playerPaymentProfileStatus } from '@/helpers/ProfileHelpers';
import { isInscriptionPath } from '@/helpers/RouteHelpers';

import session from '@/services/api/compte/session';

export default {
  name: 'Formulaire',
  components: { EditableArea, Message },
  props: {
    metadata: { type: Object, required: true },
    content: { type: Object, required: true },
  },
  i18n: {
    messages: {
      fr: {
        messageComponent: {
          detailsCompte: '/fr/mon-compte/details-du-compte',
        },
      },
      en: {
        messageComponent: {
          detailsCompte: '/en/my-account/change-details',
        },
      },
    },
  },
  data() {
    return {
      erreur: false, // Force la réactivité de flash-message
      confirmationUUID: this.uid,
      currentLanguage: getCurrentLanguage(),
      recaptchaPromise: null,
      isSubmitted: false, // Pour éviter la soumission multiple
      isSubmittedSecondaire: false, // Pour éviter la soumission multiple
      uniqueId: this.uid,
    };
  },
  computed: {
    ...mapGetters('routes', ['getUrlFromWebsitePath']),
    isConfirmationRequise() {
      return this.content.confTitre || this.content.confTexte;
    },
    btnVariant() {
      if (this.content.variantBoutonContinuer) {
        return this.content.variantBoutonContinuer;
      }
      return 'primaire';
    },
    btnVariantSecondaire() {
      if (this.content.variantBoutonSecondaire) {
        return this.content.variantBoutonSecondaire;
      }
      return 'primaire';
    },
    libBtnConfirmer() {
      return this.content.confLibConfirmer ?? this.content.libBoutonContinuer;
    },
    libBtnModifier() {
      return this.content.confLibModifier ?? this.$i18n.t('modifier');
    },
    libBtnSecondaire() {
      return this.content.typeBoutonSecondaire.libBoutonSecondaire ?? '';
    },
    isBtnSecondaire() {
      return (
        this.content.typeBoutonSecondaire &&
        /^bouton/i.test(this.content.typeBoutonSecondaire.field)
      );
    },
    isLienSecondaire() {
      return (
        this.content.typeBoutonSecondaire && /^lien/i.test(this.content.typeBoutonSecondaire.field)
      );
    },
    contentLienSecondaire() {
      if (
        !inMgnl &&
        this.content.typeBoutonSecondaire &&
        this.content.typeBoutonSecondaire.tacheSecondaire === 'sauvegarderVerificationInfoRefuse'
      ) {
        if (
          session.sessionEstOuverte() &&
          this.$store.state.profile.profileDetails &&
          this.$store.state.profile.profileDetails.status.includes(
            playerPaymentProfileStatus.UPLOAD_ID
          )
        ) {
          return this.$i18n.t('identiteVerification.libelleLienRefuserFMOUploadId');
        }
        return this.$i18n.t('identiteVerification.libelleLienRefuserFMO');
      }
      return this.content.typeBoutonSecondaire.libLienSecondaire;
    },
  },

  mounted() {
    if (this.jel_isRecaptchaActive()) {
      this.recaptchaPromise = load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
        autoHideBadge: true,
      });
    }
  },

  methods: {
    getClassBoutonContinuer() {
      if (this.content.classeBoutonContinuer) {
        if (this.content.classeBoutonContinuer == 'd-none') {
          return 'd-none';
        }
        return `d-flex align-items-center ${this.content.classeBoutonContinuer}`;
      }
      return 'd-flex align-items-center align-items-md-start';
    },
    getClassBoutonSecondaire() {
      if (
        this.content.typeBoutonSecondaire &&
        /^lien/i.test(this.content.typeBoutonSecondaire.field)
      ) {
        return `align-items-center align-items-md-start ${this.content.typeBoutonSecondaire.classeBoutonSecondaire}`;
      }
      if (
        this.content.typeBoutonSecondaire &&
        this.content.typeBoutonSecondaire.classeBoutonSecondaire
      ) {
        if (this.content.typeBoutonSecondaire.classeBoutonSecondaire == 'd-none') {
          return 'd-none';
        }
        return `d-flex align-items-center ${this.content.typeBoutonSecondaire.classeBoutonSecondaire}`;
      }
      return 'd-flex align-items-center align-items-md-start';
    },

    isNewRegistrationFlow() {
      return (
        process.env.VUE_APP_NEW_REG_FLOW === 'true' &&
        this.$route.path ===
          this.$store.getters['routes/getUrlFromWebsitePath'](PageMapping.BIENVENUE.siteUrl)
      );
    },

    onSubmit(event) {
      if (this.isSubmitted) {
        return;
      }

      /**
       * Envoi les event 'offre_bienvenue' à GA4 pendant le parcours d'inscription
       */
      const currentPath = this.$router.history.current;
      if (isInscriptionPath(currentPath.path)) {
        this.sendEventOffreBienvenueToGA4();
      }

      if (this.content.action) {
        let { action } = this.content;
        if (this.content.listeParametre) {
          action += '?';
          // TODO: Faire un split ";" et boucler
          const urlParams = new URLSearchParams(window.location.search);
          const aParam = urlParams.get(this.content.listeParametre);

          action += `${this.content.listeParametre}=${aParam}`;
        }
        document.forms[`idFormulaire${this.uniqueId}`].action = action;
        document.forms[`idFormulaire${this.uniqueId}`].method = 'post';

        if (this.content.tache) {
          // TODO: Traiter les cas d'erreur à l'exécution de la tâche.
          try {
            // TODO: Traiter erreur
            this.executerTache(this.content.tache);
            return; // A pour effet d'exécuter l'action
          } catch (error) {
            // Arrêter l'exécution de l'action
            event.preventDefault();
            event.stopPropagation();

            // TODO: Traiter Erreur
          }
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // Si confirmation requise on affiche la fenêtre
      if (this.isConfirmationRequise) {
        this.$bvModal.show(this.confirmationUUID);
      } else {
        this.formSubmit(this.content.tache);
      }
    },

    formSubmit(tache) {
      if (tache === this.content.tache) {
        this.isSubmitted = true;
      } else {
        this.isSubmittedSecondaire = true;
      }
      if (tache) {
        if (this.jel_isRecaptchaActive()) {
          this.recaptchaPromise.then((recaptcha) => {
            recaptcha.execute('registration').then((token) => {
              this.executerTache(tache, token);
            });
          });
        } else {
          this.executerTache(this.content.tache);
        }
      } else {
        this.continuer();
        this.isSubmitted = false;
        this.isSubmittedSecondaire = false;
      }
    },

    // @vuese
    // Exécuter une tâche
    executerTache(tache, options) {
      try {
        const methodTache = services.taches[tache];
        promiseWrapper(methodTache(options), process.env.VUE_APP_REST_SERVICE_TIMEOUT)
          .then(() => {
            // Happy path
            this.isSubmitted = false;
            this.continuer(null);
          })
          .catch((error) => {
            this.isSubmitted = false;
            if (error == 'EXPIRE') {
              this.continuer(PageMapping.ERREUR_GENERIQUE.siteUrl);
            } else if (error.url) {
              this.continuer(error.url);
            }
            // Sinon, on demeure sur la page courante en affichant le message
          });
      } catch (error) {
        // Ne devrait pas arriver
        this.isSubmitted = false;
        console.warn(`Tâche inconnue: ${tache}`);
      }
    },

    // Si route est nulle alors on prend la route provenant de Magnolia
    continuer(route) {
      if (this.content.action) {
        // Ne rien faire si on a une action car elle sera exécutée en toute fin de soumission
        return;
      }
      let routeContinuer = route;
      if (/^(\/\/|https|app:)/i.test(routeContinuer)) {
        window.location.href = nettoyerLienExterne(routeContinuer);
      } else {
        // S'il n'y a pas d'erreur
        if (!routeContinuer) {
          // TODO: Possiblement, déplacer ce code dans "seConnecter"
          const currentPath = this.$router.history.current;
          // Si la tâche à réaliser dans la page était une connexion et qu'il y a un paramètre de destination dans l'URL
          if (this.content.tache == 'seConnecter' && currentPath.query.dest != undefined) {
            // On traite le paramètre rentré comme une "clé" de l'objet PageMapping dans RedirectionHelpers.js
            let pageRedirection = PageMapping[currentPath.query.dest.toUpperCase()];

            // Si la clé n'est pas trouvée dans PageMapping, on fournit un autre lien au compte du client
            if (pageRedirection == undefined) {
              pageRedirection = PageMapping.DEPOT;
            }

            routeContinuer = pageRedirection.siteUrl;
          } else {
            // Route dans Magnolia
            routeContinuer =
              !isJelMobile() && this.content.routeContinuerDesktop
                ? this.content.routeContinuerDesktop
                : this.content.routeContinuer;
          }
        }

        // Supprime les QueryParams si Offre promo Acquisition sélectionnée
        if (hasPromoSelected()) {
          delete this.$router.history.current.query.os;
          delete this.$router.history.current.query.promo;
        }
        // On catch l'erreur car le guard peut faire une redirection avec les query parameters
        this.$router.push(this.getUrlFromWebsitePath(routeContinuer)).catch((error) => {
          // On catch pour éviter:
          // Uncaught (in promise) Error: Problem handling something: Error: Redirected when going from "?" to "?" via a navigation guard.
        });
      }
    },
    sendEventOffreBienvenueToGA4() {
      /**
       *  Génération du dataLayer GA4 pour les ofres de bienvenue pendant le parcours d'inscription.
       *
       *  event: "offre_bienvenue",
       *  link_text: "10$ pour tous les jeux de loteries", // Texte de l'offre sélectionnée
       *  link_id: "LOTERIES99", // ID de l'offre sélectionné (attributs aria-checked ou value)
       *  method: "default" // Méthode de sélection de l'offre (default ou select), pour indiquer si l'utilisateur a gardé le choix par défaut ou sélectionné de lui-même
       *  position: 1 // Position de l'offre sélectionnée -- 1: Supérieure gauche, 2: Supérieure droite, 3: Inférieure gauche, 4: Inférieure droite
       */
      const eventContent = {
        event: 'offre_bienvenue',
        link_text: this.$store.state.compte.libellePromo,
        link_id: this.$store.state.compte.codePromo,
        method: this.$store.state.compte.methodeChoixPromo,
        position: this.$store.state.compte.positionPromo,
      };

      // Ajout datalayer pour ajouter statistiques Event click vers Google Analytics
      gtmCustomEventByVendor(process.env.VUE_APP_IAB_ID_ANALYTICS, eventContent);
    },
  },
};
</script>

<style lang="scss" scoped>
.hauteur {
  padding: 0.5rem;

  @include media-breakpoint-down(md) {
    height: auto !important;
    padding: 0.5rem 0.75rem;
  }
}

.disabled {
  pointer-events: none;
  cursor: default;
  opacity: 0.6;
}

.canafe {
  @media (min-width: 768px) {
    flex-direction: row !important;
  }
}
.posBtnFermer {
  justify-content: flex-end;
  margin-top: 0px;
  padding-right: 0px;
  padding-top: 0px;
}
</style>
